import { inject, Injectable } from '@angular/core';
import { Sort } from '@angular/material/sort';
import { BehaviorSubject, Subject } from 'rxjs';

import { ProfileService } from '../profile/profile.service';
import { getLocalStorageSettings } from '../profile/setting.utils';
import { FilterCondition } from '../shared/filter.utils';
import { Paginated } from '../shared/models/paginated.model';
import { QueryTask, ReaderTask, TaskReport, UploadTask } from '../shared/models/task.model';
import { UserSettings } from '../shared/models/user-settings.model';

export const DEFAULT_PAGE_SIZE = 50;

export interface PaginatorConfig {
  pageIndex: number
  pageSize: number
  length: number
}

export interface FilterSortPagination {
  filter?: FilterCondition[]
  sort?: Sort
  pagination?: PaginatorConfig
}

export const defaultSort: Sort = {
  active: 'created',
  direction: 'desc',
};

export const defaultPagination: PaginatorConfig = {
  pageIndex: 0,
  pageSize: 50,
  length: 0,
};

export const defaultTasksFilterSortPagination: FilterSortPagination = {
  filter: [],
  sort: defaultSort,
  pagination: defaultPagination,
}

@Injectable()
export class TaskManagerService {

  queryTasks$ = new BehaviorSubject<Paginated<QueryTask[]>>(null);
  queryFilterSortPagination$ = new BehaviorSubject<FilterSortPagination>(defaultTasksFilterSortPagination);

  readerTasks$ = new BehaviorSubject<Paginated<ReaderTask[]>>(null);
  readerFilterSortPagination$ = new BehaviorSubject<FilterSortPagination>(defaultTasksFilterSortPagination);

  uploadTasks$ = new BehaviorSubject<Paginated<UploadTask[]>>(null);
  uploadFilterSortPagination$ = new BehaviorSubject<FilterSortPagination>(defaultTasksFilterSortPagination);

  reportQueue$ = new BehaviorSubject<Paginated<TaskReport[]>>(null);
  reportFilterSortPagination$ = new BehaviorSubject<FilterSortPagination>(defaultTasksFilterSortPagination);

  readonly loadMore$ = new Subject<void>();
  readonly hasError$ = new BehaviorSubject<boolean>(false);

  private profileService = inject(ProfileService);

  /** Get reader task tags from localStorage */
  getReaderTaskTags(): UserSettings['readerTasks']['tags'] {
    const settings = getLocalStorageSettings();
    return settings?.readerTasks?.tags || [];
  }

  /** Add new tags to reader tasks in localStorage. Ensures uniqueness using Set */
  addReaderTaskTags(tags: string[]): void {
    if (!tags?.length) {
      return;
    }

    const existingTags = this.getReaderTaskTags();
    const uniqueTags = Array.from(new Set([...existingTags, ...tags.filter(Boolean)])).sort();

    const currentSettings = getLocalStorageSettings();
    this.profileService.updateSettings({
      readerTasks: {
        ...currentSettings.readerTasks,
        tags: uniqueTags,
      },
    });
  }
}
